import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { RoleGuard } from './core/guards/role.guard';
import { ErrorPageComponent } from './layouts/components/error-page/error-page.component';
import { USERROLES } from './core/common-enums/enum';
import { LandingDashboardComponent } from './shared/landing-dashboard/landing-dashboard.component';
import { StaticWebsiteLandingPageComponent } from './shared/static-website-landing-page/static-website-landing-page.component';
import { TITLE_APP } from './core/constants/title.constants';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: '',
    component: StaticWebsiteLandingPageComponent,
    children: [
      // { path: 'dashboard', component: HomeComponent },
      { path: 'dashboard', component: LandingDashboardComponent },
      // { path: 'about', component: AboutUsComponent },
      // { path: 'contact', component: ContactUsComponent },
      // { path: 'research-team', component: ResearchTeamComponent },
      // { path: 'privacypolicy', component: PrivacyPolicyComponent },
      // { path: 'termsandconditions', component: TermsConditionComponent }
      {
        path: 'about/gemfin',
        loadComponent: () =>
          import('./shared/static-website-landing-page/about-gemfin/about-gemfin.component').then(
            (c) => c.AboutGemfinComponent
          )
      },
      {
        path: 'about/gemini-solutions',
        loadComponent: () =>
          import('./shared/static-website-landing-page/about-gemini-solutions/about-gemini-solutions.component').then(
            (c) => c.AboutGeminiSolutionsComponent
          )
      }
    ]
  },
  // Login routes
  {
    path: 'login',
    loadComponent: () =>
      import('./modules/login/login-screen/login-screen.component').then(
        (c) => c.LoginScreenComponent
      )
  },
  {
    path: 'login/forget',
    loadComponent: () =>
      import('./modules/login/forget-password/forget-password.component').then(
        (c) => c.ForgetPasswordComponent
      ),
    data: { title: TITLE_APP.FORGET_PASSWORD }
  },
  {
    path: 'login/forget-OTP',
    loadComponent: () =>
      import('./modules/login/forget-password-otp/forget-password-otp.component').then(
        (c) => c.ForgetPasswordOTPComponent
      ),
    data: { title: TITLE_APP.FORGET_PASSWORD }
  },
  {
    path: 'forget-create-password',
    loadComponent: () =>
      import('./modules/login/forget-create-password/forget-create-password.component').then(
        (c) => c.ForgetCreatePasswordComponent
      ),
    data: { title: TITLE_APP.FORGET_PASSWORD }
  },
  {
    path: 'login/sign-up',
    loadComponent: () =>
      import('./modules/login/sign-up/sign-up.component').then(
        (c) => c.SignUpComponent
      ),
    data: { title: TITLE_APP.SIGN_UP }
  },
  {
    path: 'sign-up-otp',
    loadComponent: () =>
      import('./modules/login/sign-up-otp/sign-up-otp.component').then(
        (c) => c.SignUpOtpComponent
      ),
    data: { title: TITLE_APP.SIGN_UP }
  },
  {
    path: 'sign-up-create-password',
    loadComponent: () =>
      import('./modules/login/sign-up-create-password/sign-up-create-password.component').then(
        (c) => c.SignUpCreatePasswordComponent
      ),
    data: { title: TITLE_APP.SIGN_UP }
  },
  {
    path: 'first-time-login',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/login/first-time-login/first-time-login.component').then(
        (c) => c.BrokerOnboardingComponent
      ),
    data: {
      title: TITLE_APP.ONBOARDING,
      role: [USERROLES.ADVISOR, USERROLES.BROKER_ADMIN]
    }
  },

  // Broker routes
  {
    path: 'broker',
    canActivate: [AuthGuard, RoleGuard],
    data: { role: [USERROLES.BROKER_ADMIN] },
    children: [
      {
        path: 'clients',
        loadComponent: () =>
          import('./modules/broker/broker-admin/list-of-clients/list-of-clients.component').then(
            (c) => c.ListOfClientsComponent
          ),
        data: { title: TITLE_APP.CLIENTS }
      },
      {
        path: 'report',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/report-new/report-new.component').then(
            (c) => c.ReportNewComponent
          ),
        data: { title: TITLE_APP.REPORTS }
      },
      {
        path: 'market-watch',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/market-data/market-data.component').then(
            (c) => c.MarketDataComponent
          ),
        data: { title: 'Market Watch' }
      },
      {
        path: 'reviewRequest',
        loadComponent: () =>
          import('./modules/broker/broker-admin/list-of-requests/list-of-requests.component').then(
            (c) => c.ListOfRequestsComponent
          ),
        data: { title: TITLE_APP.REVIEW_REQUEST }
      },
      {
        path: 'brokers-list',
        loadComponent: () =>
          import('./modules/broker/broker-admin/brokers-rms-management/brokers-rms-management.component').then(
            (c) => c.BrokersRmsManagementComponent
          ),
        data: { title: TITLE_APP.BROKER_LIST }
      },
      {
        path: 'advisors-list',
        loadComponent: () =>
          import('./modules/broker/broker-admin/brokers-rms-management/brokers-rms-management.component').then(
            (c) => c.BrokersRmsManagementComponent
          ),
        data: { title: TITLE_APP.ADVISOR_LIST }
      },
      {
        path: 'relationship-manager',
        loadComponent: () =>
          import('./modules/broker/broker-admin/brokers-rms-management/brokers-rms-management.component').then(
            (c) => c.BrokersRmsManagementComponent
          ),
        data: { title: TITLE_APP.RM_LIST }
      },
      {
        path: 'transaction-history',
        loadComponent: () =>
          import('./shared/broker-client-portfolio-module/broker-client-new-transactions-table/broker-client-new-transactions-table.component').then(
            (c) => c.BrokerClientNewTransactionsTableComponent
          ),
        data: { title: TITLE_APP.TRANSACTION_HISTORY, clientView: false }
      },
      {
        path: 'scheme-details/:schemeCode',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/scheme-details/scheme-details.component').then(
            (c) => c.SchemeDetailsComponent
          ),
        data: { title: TITLE_APP.SCHEME_DETAILS }
      },
      {
        path: 'onboard-broker',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/broker/onboard-broker/onboard-broker.component').then(
            (c) => c.SubBrokerOnboardingComponent
          ),
        data: { role: [USERROLES.BROKER_ADMIN], title: TITLE_APP.ONBOARDING }
      },
      {
        path: 'onboard-advisor',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/broker/advisor-onboarding/advisor-onboarding.component').then(
            (c) => c.AdvisorOnboardingComponent
          ),
        data: { role: [USERROLES.ADVISOR] }
      },
      {
        path: 'mandate',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/broker/broker-transactions/mandate-sip/mandate-sip-table/mandate-sip-table.component').then(
            (c) => c.MandateSipTableComponent
          ),
        data: {
          role: [
            USERROLES.RELATIONSHIP_MANAGER,
            USERROLES.BROKER_ADMIN,
            USERROLES.ADVISOR
          ],
          title: TITLE_APP.MANDATE
        }
      },
      {
        path: 'profile/:rmUserId',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/rm-profile-view/rm-profile-dashboard/rm-profile-dashboard.component').then(
            (c) => c.RmProfileDashboardComponent
          ),
        data: { title: TITLE_APP.PROFILE }
      },
      {
        path: 'create-ucc',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./shared/create-ucc/create-ucc.component').then(
            (c) => c.CreateUccComponent
          ),
        data: {
          role: [
            USERROLES.BROKER_ADMIN,
            USERROLES.RELATIONSHIP_MANAGER,
            USERROLES.ADVISOR
          ]
        }
      },
      {
        path: 'ucc-list',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/ucc-list/ucc-list.component').then(
            (c) => c.UccListComponent
          ),
        data: { title: TITLE_APP.UCC_LIST }
      }
    ]
  },

  // Advisor routes
  {
    path: 'advisor',
    canActivate: [AuthGuard, RoleGuard],
    data: { role: [USERROLES.ADVISOR] },
    children: [
      {
        path: 'clients',
        loadComponent: () =>
          import('./modules/broker/broker-admin/list-of-clients/list-of-clients.component').then(
            (c) => c.ListOfClientsComponent
          ),
        data: { title: TITLE_APP.CLIENTS }
      },
      {
        path: 'advisors-list',
        loadComponent: () =>
          import('./modules/broker/broker-admin/brokers-rms-management/brokers-rms-management.component').then(
            (c) => c.BrokersRmsManagementComponent
          ),
        data: { title: TITLE_APP.ADVISOR_LIST }
      },
      {
        path: 'relationship-manager',
        loadComponent: () =>
          import('./modules/broker/broker-admin/brokers-rms-management/brokers-rms-management.component').then(
            (c) => c.BrokersRmsManagementComponent
          ),
        data: { title: TITLE_APP.RM_LIST }
      },
      {
        path: 'reviewRequest',
        loadComponent: () =>
          import('./modules/broker/broker-admin/list-of-requests/list-of-requests.component').then(
            (c) => c.ListOfRequestsComponent
          ),
        data: { title: TITLE_APP.REVIEW_REQUEST }
      },
      {
        path: 'transaction-history',
        loadComponent: () =>
          import('./shared/broker-client-portfolio-module/broker-client-new-transactions-table/broker-client-new-transactions-table.component').then(
            (c) => c.BrokerClientNewTransactionsTableComponent
          ),
        data: { title: TITLE_APP.TRANSACTION_HISTORY, clientView: false }
      },
      {
        path: 'onboard-advisor',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/broker/advisor-onboarding/advisor-onboarding.component').then(
            (c) => c.AdvisorOnboardingComponent
          ),
        data: { role: [USERROLES.ADVISOR], title: TITLE_APP.ONBOARDING }
      },
      {
        path: 'onboard-rm',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/relationship-manager/rm-onboarding/rm-onboarding.component').then(
            (c) => c.RmOnboardingComponent
          ),
        data: { role: [USERROLES.ADVISOR], title: TITLE_APP.ONBOARDING }
      },
      // {
      //   path: 'advisors-rms-management',
      //   loadComponent: () =>
      //     import(
      //       './modules/broker/broker-admin/brokers-rms-management/brokers-rms-management.component'
      //     ).then((c) => c.BrokersRmsManagementComponent),
      //   data: { title: TITLE_APP.ADVISOR_LIST }
      // },
      {
        path: 'mandate',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/broker/broker-transactions/mandate-sip/mandate-sip-table/mandate-sip-table.component').then(
            (c) => c.MandateSipTableComponent
          ),
        data: {
          role: [
            USERROLES.RELATIONSHIP_MANAGER,
            USERROLES.BROKER_ADMIN,
            USERROLES.ADVISOR
          ],
          title: TITLE_APP.MANDATE
        }
      },
      {
        path: 'profile/:rmUserId',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/rm-profile-view/rm-profile-dashboard/rm-profile-dashboard.component').then(
            (c) => c.RmProfileDashboardComponent
          ),
        data: { title: TITLE_APP.PROFILE }
      },
      {
        path: 'create-ucc',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./shared/create-ucc/create-ucc.component').then(
            (c) => c.CreateUccComponent
          ),
        data: {
          role: [
            USERROLES.BROKER_ADMIN,
            USERROLES.RELATIONSHIP_MANAGER,
            USERROLES.ADVISOR
          ]
        }
      },
      {
        path: 'ucc-list',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/ucc-list/ucc-list.component').then(
            (c) => c.UccListComponent
          ),
        data: { title: TITLE_APP.UCC_LIST }
      },
      {
        path: 'scheme-analytics',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/scheme-analytics/scheme-analytics.component').then(
            (c) => c.SchemeAnalyticsComponent
          ),
        data: { title: TITLE_APP.SCHEME_ANALYTICS }
      },
      {
        path: 'scheme-details/:schemeCode',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/scheme-details/scheme-details.component').then(
            (c) => c.SchemeDetailsComponent
          ),
        data: { title: TITLE_APP.SCHEME_DETAILS }
      },
      {
        path: 'report',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/report-new/report-new.component').then(
            (c) => c.ReportNewComponent
          ),
        data: { title: TITLE_APP.REPORTS }
      },
      {
        path: 'market-watch',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/market-data/market-data.component').then(
            (c) => c.MarketDataComponent
          ),
        data: { title: 'Market Watch' }
      }
    ]
  },

  // Product Admin routes
  {
    path: 'product-admin',
    canActivate: [AuthGuard, RoleGuard],
    data: { role: USERROLES.ADMIN },
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./modules/product-admin/product-admin-dashboard/product-admin-dashboard.component').then(
            (c) => c.ProductAdminDashboardComponent
          ),
        data: { title: TITLE_APP.HOME }
      },
      {
        path: 'member-onboarding',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/product-admin/add-new-broker/add-new-broker.component').then(
            (c) => c.AddNewBrokerComponent
          ),
        data: { title: TITLE_APP.ONBOARDING }
      },
      {
        path: 'dbf-file-viewer',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/product-admin/dbf-data-viewer/dbf-data-viewer.component').then(
            (c) => c.DbfDataViewerComponent
          ),
        data: { title: 'Data File Viewer' }
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      }
    ]
  },

  // Relationship Manager routes
  {
    path: 'relationshipManager',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'clients',
        loadComponent: () =>
          import('./modules/broker/broker-admin/list-of-clients/list-of-clients.component').then(
            (c) => c.ListOfClientsComponent
          ),
        data: { title: TITLE_APP.CLIENTS }
      },
      {
        path: 'onboard-rm',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/relationship-manager/rm-onboarding/rm-onboarding.component').then(
            (c) => c.RmOnboardingComponent
          ),
        data: { role: [USERROLES.BROKER_ADMIN], title: TITLE_APP.ONBOARDING }
      },
      {
        path: 'profile/:rmUserId',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/rm-profile-view/rm-profile-dashboard/rm-profile-dashboard.component').then(
            (c) => c.RmProfileDashboardComponent
          ),
        data: { title: TITLE_APP.PROFILE }
      },
      {
        path: 'scheme-analytics',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/scheme-analytics/scheme-analytics.component').then(
            (c) => c.SchemeAnalyticsComponent
          ),
        data: { title: TITLE_APP.SCHEME_ANALYTICS }
      },
      {
        path: 'scheme-details/:schemeCode',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/scheme-details/scheme-details.component').then(
            (c) => c.SchemeDetailsComponent
          ),
        data: { title: TITLE_APP.SCHEME_DETAILS }
      },
      {
        path: 'mandate',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./modules/broker/broker-transactions/mandate-sip/mandate-sip-table/mandate-sip-table.component').then(
            (c) => c.MandateSipTableComponent
          ),
        data: {
          role: [
            USERROLES.RELATIONSHIP_MANAGER,
            USERROLES.BROKER_ADMIN,
            USERROLES.ADVISOR
          ],
          title: TITLE_APP.MANDATE
        }
      },
      {
        path: 'create-ucc',
        canActivate: [AuthGuard, RoleGuard],
        loadComponent: () =>
          import('./shared/create-ucc/create-ucc.component').then(
            (c) => c.CreateUccComponent
          ),
        data: {
          role: [
            USERROLES.BROKER_ADMIN,
            USERROLES.RELATIONSHIP_MANAGER,
            USERROLES.ADVISOR
          ]
        }
      },
      {
        path: 'ucc-list',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./shared/ucc-list/ucc-list.component').then(
            (c) => c.UccListComponent
          ),
        data: { title: TITLE_APP.UCC_LIST }
      },
      {
        path: 'report',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/report-new/report-new.component').then(
            (c) => c.ReportNewComponent
          ),
        data: { title: TITLE_APP.REPORTS }
      },
      {
        path: 'market-watch',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./modules/broker/utilities/market-data/market-data.component').then(
            (c) => c.MarketDataComponent
          ),
        data: { title: 'Market Watch' }
      }
    ]
  },

  // Client routes - Fixed to work without parent layout

  {
    path: 'client/client-onboarding',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/client/new-cliient-onboarding/client-onboard/client-onboard.component').then(
        (c) => c.ClientOnboardComponent
      ),
    data: {
      role: [
        USERROLES.RELATIONSHIP_MANAGER,
        USERROLES.BROKER_ADMIN,
        USERROLES.CLIENT,
        USERROLES.ADVISOR
      ],
      title: TITLE_APP.ONBOARDING
    }
  },
  {
    path: 'client/new-client-onboarding',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/client/new-cliient-onboarding/client-onboard/client-onboard.component').then(
        (c) => c.ClientOnboardComponent
      ),
    data: {
      role: [
        USERROLES.RELATIONSHIP_MANAGER,
        USERROLES.BROKER_ADMIN,
        USERROLES.CLIENT,
        USERROLES.ADVISOR
      ],
      title: TITLE_APP.ONBOARDING
    }
  },
  {
    path: 'client/edit-client-details',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/client/new-cliient-onboarding/client-onboard/client-onboard.component').then(
        (c) => c.ClientOnboardComponent
      ),
    data: {
      role: [
        USERROLES.RELATIONSHIP_MANAGER,
        USERROLES.BROKER_ADMIN,
        USERROLES.CLIENT,
        USERROLES.ADVISOR
      ],
      title: TITLE_APP.ONBOARDING
    }
  },
  {
    path: 'client/edit-details/:id',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./modules/client/client-edit-details/client-edit-details.component').then(
        (c) => c.ClientEditDetailsComponent
      ),
    data: { title: TITLE_APP.PROFILE }
  },
  {
    path: 'client/profile',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/portfolio-profile-tab/portfolio-profile-tab.component').then(
        (c) => c.PortfolioProfileTabComponent
      ),
    data: { title: TITLE_APP.PROFILE }
  },
  {
    path: 'client/holdings',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-holdings-table/broker-client-holdings-table.component').then(
        (c) => c.BrokerClientHoldingsTableComponent
      )
  },
  {
    path: 'client/transactions',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-new-transactions-table/broker-client-new-transactions-table.component').then(
        (c) => c.BrokerClientNewTransactionsTableComponent
      ),
    data: { title: TITLE_APP.TRANSACTION }
  },
  {
    path: 'client/mandate',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./modules/broker/broker-transactions/mandate-sip/mandate-sip-table/mandate-sip-table.component').then(
        (c) => c.MandateSipTableComponent
      ),
    data: { title: TITLE_APP.TRANSACTION }
  },
  {
    path: 'client/report',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/broker/report-new/report-new.component').then(
        (c) => c.ReportNewComponent
      ),
    data: { role: [USERROLES.CLIENT], title: TITLE_APP.REPORTS }
  },
  {
    path: 'client/riskprofile',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-risk-profile-component/broker-client-risk-profile-component.component').then(
        (c) => c.BrokerClientRiskProfileComponentComponent
      ),
    data: { title: TITLE_APP.PROFILE }
  },
  {
    path: 'client/riskprofileresult',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/risk-profile-result/risk-profile-result.component').then(
        (c) => c.RiskProfileResultComponent
      ),
    data: { title: TITLE_APP.PROFILE }
  },

  // Shared/Global routes (previously in SharedRoutingModule)
  {
    path: 'client-portfolio/:id',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-portfolio-dashboard/broker-client-portfolio-dashboard.component').then(
        (c) => c.BrokerClientPortfolioDashboardComponent
      ),
    data: { title: TITLE_APP.PROFILE }
  },
  {
    path: 'client-portfolio/:id/:role',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-portfolio-dashboard/broker-client-portfolio-dashboard.component').then(
        (c) => c.BrokerClientPortfolioDashboardComponent
      ),
    data: { title: TITLE_APP.PROFILE }
  },
  {
    path: 'broker-portfolio/:id',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-portfolio-dashboard/broker-client-portfolio-dashboard.component').then(
        (c) => c.BrokerClientPortfolioDashboardComponent
      ),
    data: {
      role: [
        USERROLES.RELATIONSHIP_MANAGER,
        USERROLES.BROKER_ADMIN,
        USERROLES.CLIENT,
        USERROLES.ADVISOR
      ],
      title: TITLE_APP.PROFILE
    }
  },
  {
    path: 'transaction/:selectedAction',
    loadComponent: () =>
      import('./modules/broker/broker-transactions/broker-additional-transaction/broker-additional-transaction.component').then(
        (c) => c.BrokerAdditionalTransactionComponent
      ),
    data: { title: TITLE_APP.TRANSACTION },
    runGuardsAndResolvers: 'always'
  },
  {
    path: 'fresh/transaction/:selectedAction',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/broker/broker-transactions/broker-transcation-details/broker-transcation-details.component').then(
        (c) => c.BrokerTranscationDetailsComponent
      ),
    data: {
      role: [
        USERROLES.RELATIONSHIP_MANAGER,
        USERROLES.BROKER_ADMIN,
        USERROLES.CLIENT,
        USERROLES.ADVISOR
      ],
      title: TITLE_APP.TRANSACTION
    }
  },
  {
    path: 'risk-profile',
    loadComponent: () =>
      import('./shared/risk-profile/risk-profile.component').then(
        (c) => c.RiskProfileComponent
      ),
    data: { title: TITLE_APP.RISK_PROFILE }
  },
  {
    path: 'risk-profile-result',
    loadComponent: () =>
      import('./shared/risk-profile-result/risk-profile-result.component').then(
        (c) => c.RiskProfileResultComponent
      ),
    data: { title: TITLE_APP.RISK_PROFILE }
  },
  {
    path: 'broker-client-risk-profile-component',
    loadComponent: () =>
      import('./shared/broker-client-portfolio-module/broker-client-risk-profile-component/broker-client-risk-profile-component.component').then(
        (c) => c.BrokerClientRiskProfileComponentComponent
      ),
    data: { title: TITLE_APP.RISK_PROFILE }
  },
  {
    path: 'risk-profile-form/:clientId',
    loadComponent: () =>
      import('./shared/risk-profile-form/risk-profile-form.component').then(
        (c) => c.RiskProfileFormComponent
      )
  },
  // Universal utility routes (accessible to Broker Admin, Advisor, RM)
  {
    path: 'scheme-analytics',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/broker/utilities/scheme-analytics/scheme-analytics.component').then(
        (c) => c.SchemeAnalyticsComponent
      ),
    data: {
      role: [
        USERROLES.BROKER_ADMIN,
        USERROLES.ADVISOR,
        USERROLES.RELATIONSHIP_MANAGER
      ],
      title: TITLE_APP.SCHEME_ANALYTICS
    }
  },
  {
    path: 'scheme-details/:schemeCode',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/broker/utilities/scheme-details/scheme-details.component').then(
        (c) => c.SchemeDetailsComponent
      ),
    data: {
      role: [
        USERROLES.BROKER_ADMIN,
        USERROLES.ADVISOR,
        USERROLES.RELATIONSHIP_MANAGER
      ],
      title: TITLE_APP.SCHEME_DETAILS
    }
  },
  {
    path: 'market-watch',
    canActivate: [AuthGuard, RoleGuard],
    loadComponent: () =>
      import('./modules/broker/utilities/market-data/market-data.component').then(
        (c) => c.MarketDataComponent
      ),
    data: {
      role: [
        USERROLES.BROKER_ADMIN,
        USERROLES.ADVISOR,
        USERROLES.RELATIONSHIP_MANAGER
      ],
      title: 'Market Watch'
    }
  },

  // Static website routes
  {
    path: 'about',
    loadComponent: () =>
      import('./shared/static-website-landing-page/about-us/about-us.component').then(
        (c) => c.AboutUsComponent
      )
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./shared/static-website-landing-page/contact-us/contact-us.component').then(
        (c) => c.ContactUsComponent
      )
  },
  {
    path: 'research-team',
    loadComponent: () =>
      import('./shared/static-website-landing-page/research-team/research-team.component').then(
        (c) => c.ResearchTeamComponent
      )
  },
  {
    path: 'privacypolicy',
    loadComponent: () =>
      import('./shared/static-website-landing-page/privacy-policy/privacy-policy.component').then(
        (c) => c.PrivacyPolicyComponent
      )
  },
  {
    path: 'termsandconditions',
    loadComponent: () =>
      import('./shared/static-website-landing-page/terms-condition/terms-condition.component').then(
        (c) => c.TermsConditionComponent
      )
  },
  {
    path: '**',
    component: ErrorPageComponent
    //pathMatch: 'full',
  }
];

export { routes };
